const areEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const isNewItem = (item = {}) => item.is_new === 1 || item.isNew === true;
const isAddedItem = (item = {}) => item.is_added === true || item.is_added === 1;
const isTrackableAddedItem = (item = {}) => isNewItem(item) || isAddedItem(item);

const getSection = (document = {}, sectionKey) => (
  document.sections && document.sections[sectionKey]
    ? document.sections[sectionKey]
    : {}
);

const getItems = (section = {}) => (
  section.data && Array.isArray(section.data.method_items)
    ? section.data.method_items
    : []
);

const getSelectedValue = (section = {}, item = {}) => {
  const enabled = section.data && section.data.enabled;
  if (enabled && Object.prototype.hasOwnProperty.call(enabled, `item_${item.id}`)) {
    return Number(enabled[`item_${item.id}`]) ? 1 : 0;
  }
  return Number(item.selected) ? 1 : 0;
};

const itemValue = (item = {}) => ({
  id: item.id,
  method_item: item.method_item,
  linked_documents: item.linked_documents,
  selected: Number(item.selected) ? 1 : 0,
  ...(isNewItem(item) ? { is_new: 1 } : {}),
  ...(isAddedItem(item) ? { is_added: true } : {})
});

const createSectionItem = (sectionKey, sectionLabel) => ({
  item_id: null,
  item_label: null,
  section_changes: [{
    section_key: sectionKey,
    section_label: sectionLabel,
    changes: []
  }]
});

export const buildMethodItemSectionsChanges = (initialDocument = {}, submittedDocument = {}) => {
  const sectionKeys = Array.from(new Set([
    ...Object.keys(initialDocument.sections || {}),
    ...Object.keys(submittedDocument.sections || {})
  ])).filter((key) => key.indexOf("method_item_type_") === 0);
  const items = [];

  sectionKeys.forEach((sectionKey) => {
    const initialSection = getSection(initialDocument, sectionKey);
    const submittedSection = getSection(submittedDocument, sectionKey);
    const sectionLabel = submittedSection.display_name || initialSection.display_name || sectionKey;
    const sectionItem = createSectionItem(sectionKey, sectionLabel);
    const changes = sectionItem.section_changes[0].changes;
    const initialData = initialSection.data || {};
    const submittedData = submittedSection.data || {};

    if (!areEqual(Number(initialData.toggle), Number(submittedData.toggle))) {
      changes.push({
        field_name: "toggle",
        field_label: `${sectionLabel} Toggle`,
        change_type: "inline",
        action: Number(submittedData.toggle) === 1 ? "toggled_on" : "toggled_off",
        previous_value: Number(initialData.toggle),
        new_value: Number(submittedData.toggle)
      });
    }

    const initialItems = getItems(initialSection);
    const submittedItems = getItems(submittedSection);
    const initialById = new Map(initialItems.map((item) => [item.id, item]));
    const submittedById = new Map(submittedItems.map((item) => [item.id, item]));
    const itemIds = new Set([...initialById.keys(), ...submittedById.keys()]);
    const added = [];
    const deleted = [];
    const toggledOn = [];
    const toggledOff = [];
    const updated = [];

    itemIds.forEach((itemId) => {
      const previous = initialById.get(itemId);
      const next = submittedById.get(itemId);
      if (!previous || !next) {
        const item = next || previous;
        if (isTrackableAddedItem(item)) {
          const changedItem = itemValue(item);
          changedItem.selected = getSelectedValue(next ? submittedSection : initialSection, item);
          (next ? added : deleted).push(changedItem);
        }
        return;
      }

      if (isAddedItem(previous) || isAddedItem(next) || (!isNewItem(previous) && !isNewItem(next))) {
        const previousSelected = getSelectedValue(initialSection, previous);
        const nextSelected = getSelectedValue(submittedSection, next);
        if (previousSelected !== nextSelected) {
          const changedItem = itemValue(next);
          const previousItem = itemValue(previous);
          previousItem.selected = previousSelected;
          changedItem.selected = nextSelected;
          (nextSelected === 1 ? toggledOn : toggledOff).push({
            previous: previousItem,
            next: changedItem
          });
        }
        return;
      }

      if (isNewItem(previous) && isNewItem(next)) {
        [
          ["method_item", "Method Item", "inline"],
          ["linked_documents", "Linked Documents", "multiple_items"]
        ].forEach(([fieldName, fieldLabel, changeType]) => {
          if (!areEqual(previous[fieldName], next[fieldName])) {
            updated.push({
              previous: itemValue(previous),
              next: itemValue(next),
              fieldName,
              fieldLabel,
              changeType
            });
          }
        });
        const previousSelected = getSelectedValue(initialSection, previous);
        const nextSelected = getSelectedValue(submittedSection, next);
        if (previousSelected !== nextSelected) {
          const changedItem = itemValue(next);
          const previousItem = itemValue(previous);
          previousItem.selected = previousSelected;
          changedItem.selected = nextSelected;
          (nextSelected === 1 ? toggledOn : toggledOff).push({
            previous: previousItem,
            next: changedItem
          });
        }
      }
    });

    const addMultipleItemsChange = (action, entries) => {
      if (entries.length === 0) return;
      changes.push({
        field_name: "method_items",
        field_label: sectionLabel,
        change_type: "multiple_items",
        action,
        previous_value: entries.map((entry) => entry.previous),
        new_value: entries.map((entry) => entry.next)
      });
    };

    if (added.length > 0) {
      changes.push({
        field_name: "method_items",
        field_label: sectionLabel,
        change_type: "multiple_items",
        action: "added",
        previous_value: [],
        new_value: added
      });
    }
    if (deleted.length > 0) {
      changes.push({
        field_name: "method_items",
        field_label: sectionLabel,
        change_type: "multiple_items",
        action: "deleted",
        previous_value: deleted,
        new_value: []
      });
    }
    addMultipleItemsChange("toggled_on", toggledOn);
    addMultipleItemsChange("toggled_off", toggledOff);
    if (updated.length > 0) {
      const updatedById = new Map();
      updated.forEach((entry) => {
        if (!updatedById.has(entry.next.id)) {
          updatedById.set(entry.next.id, {
            previous: entry.previous,
            next: entry.next
          });
        }
      });
      changes.push({
        field_name: "method_items",
        field_label: sectionLabel,
        change_type: "multiple_items",
        action: "updated",
        previous_value: Array.from(updatedById.values()).map((entry) => entry.previous),
        new_value: Array.from(updatedById.values()).map((entry) => entry.next)
      });
    }

    if (changes.length > 0) {
      items.push(sectionItem);
    }
  });

  return items;
};
